import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Loader2, Crosshair, AlertTriangle, MessageSquare, Layers, Palette } from "lucide-react";
import { LookSwatch, kindLabel } from "./takeoffShared";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const SUMMARY = [["measured", "Measured"], ["not_measured", "Not measured, and why"], ["tie_out", "Tie-out to the sheet"], ["friction", "Friction points"]];

function ScaleBlock({ s, readOnly, onSettle }) {
  const c = s?.current;
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="acct-scale">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><Crosshair className="w-3 h-3" /> Scale</div>
      {s?.px_per_ft ? (
        <div className="text-sm text-slate-800"><b>{Number(s.px_per_ft).toFixed(3)} px per ft</b>{c?.basis ? <> · based on <i>{c.basis}</i></> : null}
          {c && <div className="text-[11px] text-slate-500">set by {c.by}{c.via === "claude" ? " via Claude" : ""} · {new Date(c.at).toLocaleString()} · {c.length_ft} ft between two points</div>}
        </div>
      ) : <div className="text-sm text-slate-500">No scale yet: lengths and areas can't be measured.</div>}
      {s?.flag && (
        <div className="mt-2 rounded bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800" data-testid="acct-scale-flag">
          <div className="flex items-center gap-1 font-semibold"><AlertTriangle className="w-3 h-3" /> {s.flag.message}</div>
          {!readOnly && (s.calibrations || []).filter((x) => s.flag.cal_ids.includes(x.cal_id)).map((x) => (
            <button key={x.cal_id} onClick={() => onSettle(x.cal_id)} className="mt-1 mr-2 underline" data-testid={`acct-settle-${x.cal_id}`}>Use {x.px_per_ft} ({x.basis}, {x.by})</button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AccountedPanel({ open, onOpenChange, bidId, drawingId, readOnly, onChanged }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    if (!drawingId) return;
    setD(null);
    api.get(`/bids/${bidId}/drawings/${drawingId}/accounted`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [bidId, drawingId]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const settle = async (cal_id) => {
    try { await api.post(`/bids/${bidId}/drawings/${drawingId}/settle-scale`, { cal_id }); toast.success("Scale settled"); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not settle the scale"); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="acct-panel">
        <SheetHeader>
          <SheetTitle>What is accounted for here</SheetTitle>
          <SheetDescription>{d?.name || "This drawing"}: every layer, what it feeds and why.</SheetDescription>
        </SheetHeader>
        {!d ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#003E87]" /></div> : d.error ? <div className="text-sm text-rose-600 py-6">Could not load this drawing.</div> : (
          <div className="space-y-4 mt-4">
            <ScaleBlock s={d.scale} readOnly={readOnly} onSettle={settle} />
            <div className="rounded-lg border border-slate-200 p-3" data-testid="acct-summary">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Takeoff summary</div>
              {!d.summary ? <div className="text-sm text-slate-500">No summary written for this drawing yet.</div> : (
                <>
                  {SUMMARY.map(([k, label]) => (d.summary[k] || []).length > 0 && (
                    <div key={k} className="mt-1.5"><div className="text-xs font-semibold text-slate-700">{label}</div>
                      <ul className="list-disc ml-4 text-sm text-slate-700">{d.summary[k].map((x, i) => <li key={i}>{x}</li>)}</ul></div>
                  ))}
                  <div className="text-[11px] text-slate-400 mt-1">by {d.summary.by}{d.summary.via === "claude" ? " via Claude" : ""} · {new Date(d.summary.at).toLocaleString()}</div>
                </>
              )}
            </div>
            <div className="rounded-lg border border-slate-200" data-testid="acct-layers">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 px-3 pt-3 flex items-center gap-1"><Layers className="w-3 h-3" /> Layers on this sheet</div>
              {d.layers.length === 0 ? <div className="text-sm text-slate-500 p-3">Nothing marked on this sheet yet.</div> : d.layers.map((l) => (
                <div key={l.layer_id || "none"} className="px-3 py-2 border-t border-slate-100 first:border-t-0" data-testid={`acct-layer-${l.layer_id}`}>
                  <div className="flex items-baseline gap-2 text-sm">{l.style && <LookSwatch look={l.style} type={l.style.measure_type} testid={`acct-swatch-${l.layer_id}`} />}<b className="text-slate-800 truncate">{l.layer}</b>
                    <span className="ml-auto font-mono text-slate-700 shrink-0">{l.marks} mark{l.marks === 1 ? "" : "s"} · {l.quantity_here} {l.unit || ""}</span></div>
                  {l.feeds.map((f) => <div key={f.line_id} className="text-xs text-slate-500">{f.line_id === l.layer_id ? "this line" : <>feeds <b className="text-slate-700">{f.name}</b></>}: {f.quantity} {f.unit || ""} · {money(f.price)}</div>)}
                </div>
              ))}
            </div>
            {(d.legend || []).length > 0 && (
              <div className="rounded-lg border border-slate-200 p-3" data-testid="acct-legend">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><Palette className="w-3 h-3" /> Legend</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">{d.legend.map((e) => (
                  <div key={e.kind} className="flex items-center gap-2 text-xs text-slate-700" data-testid={`acct-legend-${e.kind}`}><LookSwatch look={e} type={e.measure_type || "count"} testid={`acct-legend-sw-${e.kind}`} />{kindLabel(e.kind)}</div>
                ))}</div>
              </div>
            )}
            <div className="rounded-lg border border-slate-200 p-3" data-testid="acct-notes">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Notes ({d.notes.length})</div>
              {d.notes.length === 0 ? <div className="text-sm text-slate-500">No notes on this drawing.</div> : d.notes.map((n) => n.comments.map((c) => (
                <div key={c.comment_id} className="text-sm text-slate-700 py-1"><span>{c.text}</span>
                  <div className="text-[11px] text-slate-400">{c.author_name}{c.via === "claude" ? " via Claude" : ""} · {new Date(c.created_at).toLocaleString()}</div></div>
              )))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
